import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { SpeakerTestimonial } from "@/content/speaking";

function withHighlights(text: string, phrases: string[]) {
  if (phrases.length === 0) return text;
  const escaped = phrases.map((phrase) => phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = text.split(new RegExp(`(${escaped.join("|")})`, "g"));
  return parts.map((part, index) =>
    phrases.includes(part) ? (
      <strong key={`${part}-${index}`} className="font-semibold text-ink">
        {part}
      </strong>
    ) : (
      <span key={`${part}-${index}`}>{part}</span>
    ),
  );
}

export function SpeakerTestimonialCard({ item }: { item: SpeakerTestimonial }) {
  const [open, setOpen] = useState(false);

  return (
    <figure className="card-soft flex min-w-0 flex-col overflow-hidden rounded-sm border border-steel bg-background p-6 md:p-7">
      <div data-testimonial-header>
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-4">
        {item.photo ? (
          <img
            src={item.photo}
            alt={item.name}
            width={64}
            height={64}
            loading="lazy"
            className="h-16 w-16 shrink-0 rounded-full object-cover"
          />
        ) : (
          <div
            aria-hidden="true"
            className="grid h-16 w-16 shrink-0 place-items-center rounded-full border border-steel bg-steel-soft text-lg font-semibold text-ink"
          >
            {item.initials}
          </div>
        )}
        <figcaption className="min-w-0 break-words">
          <p className="text-base font-medium text-ink">{item.name}</p>
          {/* Podpis autorki pokazujemy w całości — to jej tekst, nie skracamy go. */}
          <p className="text-sm text-ink-soft" title={item.role}>
            {withHighlights(item.role, item.highlights ?? [])}
          </p>
        </figcaption>
        </div>
      </div>
      <blockquote className="mt-6 space-y-4 text-base leading-relaxed text-ink/80">
        {/* Zajawka przytnięta do 4 linii, jak w kartach rekomendacji masterminda,
            żeby oba rzędy kart miały równą wysokość; rozwinięcie pokazuje pełny tekst. */}
        {open && item.fullText ? (
          item.fullText.map((paragraph) => (
            <p key={paragraph}>{withHighlights(paragraph, item.highlights ?? [])}</p>
          ))
        ) : (
          <p className={open ? undefined : "line-clamp-4"}>
            {withHighlights(item.quote, item.highlights ?? [])}
          </p>
        )}
        {open &&
          item.rest.map((paragraph) => (
            <p key={paragraph}>{withHighlights(paragraph, item.highlights ?? [])}</p>
          ))}
      </blockquote>
      {open && item.image && (
        <img
          src={item.image.url}
          alt={item.image.alt}
          loading="lazy"
          className="mt-4 w-full rounded-sm border border-steel"
        />
      )}
      {open && item.pdf && (
        <a
          href={item.pdf.url}
          download
          className="mt-4 inline-flex min-h-11 items-center self-start text-sm font-medium text-ink underline underline-offset-4 hover:text-coral-text"
        >
          {item.pdf.label}
        </a>
      )}
      <Button
        type="button"
        variant="link"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="mt-auto h-auto min-h-11 items-end self-start px-0 pt-6 text-base font-medium text-ink underline underline-offset-4 transition-colors hover:text-coral-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
      >
        {open ? "Zwiń" : "Czytaj więcej"}
      </Button>
    </figure>
  );
}